import {
  arrayRemove,
  collection,
  doc,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  documentId,
} from 'firebase/firestore';
import { db } from '@/firebase/init';
import { ParseResult } from 'papaparse';
import { chunk, concat } from 'lodash';
import {
  Keyword,
  KeywordFromStore,
  RawKeywordCSVData,
  TagFromStore,
} from '@/types/types';
import { ITEMS_PER_PAGE } from '@/utils/constants';
import { getWorkspaceKeywordsPath } from '@/utils/queryPaths';
import { keywordsCSVTagsSchema } from '@/utils/schemas';

export class KeywordsService {
  static async updateTags(
    tags: string[],
    keywordId: string,
    workspaceId: string,
    connectorId: string,
  ) {
    await updateDoc(
      doc(db, getWorkspaceKeywordsPath(workspaceId, connectorId), keywordId),
      {
        tags,
      },
    );
    return null;
  }

  static async deleteKeyword(
    keywordId: string,
    workspaceId: string,
    connectorId: string,
  ) {
    const batch = writeBatch(db);
    batch.delete(doc(db, getWorkspaceKeywordsPath(workspaceId, connectorId), keywordId));
    await batch.commit();
    return null;
  }

  static async deleteKeywords(
    keywordsIds: { id: string }[],
    workspaceId: string,
    connectorId: string,
  ) {
    const batch = writeBatch(db);
    keywordsIds.forEach((keyword) => {
      batch.delete(
        doc(db, getWorkspaceKeywordsPath(workspaceId, connectorId), keyword.id),
      );
    });
    await batch.commit();
    return null;
  }

  static async deleteTag(
    tagId: string,
    keywordId: string,
    workspaceId: string,
    connectorId: string,
  ) {
    await updateDoc(
      doc(db, getWorkspaceKeywordsPath(workspaceId, connectorId), keywordId),
      {
        tags: arrayRemove(tagId),
      },
    );
    return null;
  }

  static async getAllKeywords(workspaceId: string, connectorId: string) {
    const keywords: KeywordFromStore[] = [];
    const snapshot = await getDocs(
      query(
        collection(db, getWorkspaceKeywordsPath(workspaceId, connectorId)),
        orderBy('keyword'),
      ),
    );
    if (!snapshot) return [];
    snapshot.forEach((keyword) =>
      keywords.push({ ...keyword.data(), id: keyword.id } as KeywordFromStore),
    );
    return keywords;
  }

  static async changeLanguage(
    language: string,
    keywordId: string,
    workspaceId: string,
    connectorId: string,
  ) {
    await updateDoc(
      doc(collection(db, getWorkspaceKeywordsPath(workspaceId, connectorId)), keywordId),
      { language },
    );
  }

  static async getFirstPage(workspaceId: string, connectorId: string) {
    const keywordsQuery = query(
      collection(db, getWorkspaceKeywordsPath(workspaceId, connectorId)),
      orderBy('keyword'),
      limit(ITEMS_PER_PAGE),
    );
    const [keywordsSnapshot, totalCount] = await Promise.all([
      getDocs(keywordsQuery),
      KeywordsService.getTotalCount(workspaceId, connectorId),
    ]);
    const keywords: KeywordFromStore[] = Array(totalCount.data().count);
    keywordsSnapshot.docs.forEach((keyword, index) => {
      keywords[index] = { ...keyword.data(), id: keyword.id } as KeywordFromStore;
    });
    return keywords;
  }

  static async getTotalCount(workspaceId: string, connectorId: string) {
    return await getCountFromServer(
      query(collection(db, getWorkspaceKeywordsPath(workspaceId, connectorId))),
    );
  }

  static async createKeywords(
    parsedKeywords: string[],
    language: string,
    tags: string[],
    workspaceId: string,
    userId: string,
    connectorId: string,
    locationCode: number,
  ) {
    const batch = writeBatch(db);
    parsedKeywords.forEach((keyword) => {
      const keywordRef = doc(
        collection(db, getWorkspaceKeywordsPath(workspaceId, connectorId)),
      );
      const newKeyword = {
        keyword,
        language,
        tags,
        createdAt: serverTimestamp(),
        createdById: userId,
        locationCode,
      };
      batch.set(keywordRef, newKeyword);
    });

    await batch.commit();
    return null;
  }

  static async getPagesOfKeywords(
    workspaceId: string,
    page: number,
    itemsPerPage: number,
    connectorId: string,
  ) {
    const keywordsSnapshot = await getDocs(
      query(
        collection(db, getWorkspaceKeywordsPath(workspaceId, connectorId)),
        orderBy('keyword'),
        limit(page * itemsPerPage),
      ),
    );
    return keywordsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        } as KeywordFromStore),
    );
  }

  private static async processSmallDataset(
    results: Pick<ParseResult<RawKeywordCSVData>, 'data'>,
    tags: TagFromStore[],
    workspaceId: string,
    userId: string,
    connectorId: string,
    locationCode: number,
  ) {
    const batch = writeBatch(db);
    results.data.forEach((row) => {
      const tagsIds = KeywordsService.extractTagsIds(row, tags);
      const parsedTags = keywordsCSVTagsSchema.parse(tagsIds);
      const newKeyword: Keyword = {
        keyword: row.keyword,
        language: row.language,
        tags: parsedTags,
      };
      batch.set(doc(collection(db, getWorkspaceKeywordsPath(workspaceId, connectorId))), {
        ...newKeyword,
        createdAt: serverTimestamp(),
        createdById: userId,
        locationCode,
      });
    });
    await batch.commit();
  }

  private static async processLargeDataset(
    results: ParseResult<RawKeywordCSVData>,
    tags: TagFromStore[],
    workspaceId: string,
    userId: string,
    connectorId: string,
    locationCode: number,
  ) {
    const dataBatches = chunk(results.data, 500);
    await Promise.all(
      dataBatches.map(
        async (batch) =>
          await KeywordsService.processSmallDataset(
            { data: batch },
            tags,
            workspaceId,
            userId,
            connectorId,
            locationCode,
          ),
      ),
    );
  }

  static async createKeywordsFromCSVData(
    results: ParseResult<RawKeywordCSVData>,
    tags: TagFromStore[],
    workspaceId: string,
    userId: string,
    connectorId: string,
    locationCode: number,
  ) {
    if (results.data.length < 500) {
      await KeywordsService.processSmallDataset(
        results,
        tags,
        workspaceId,
        userId,
        connectorId,
        locationCode,
      );
    } else {
      await KeywordsService.processLargeDataset(
        results,
        tags,
        workspaceId,
        userId,
        connectorId,
        locationCode,
      );
    }
    return null;
  }

  static async getKeywordsByIds(
    workspaceId: string,
    keywordsIds: string[],
    connectorId: string,
  ) {
    const batches = chunk(keywordsIds, 10);
    const responses = await Promise.all(
      batches.map(async (batch) => {
        const keywordsSnapshot = await getDocs(
          query(
            collection(db, getWorkspaceKeywordsPath(workspaceId, connectorId)),
            where(documentId(), 'in', batch),
            limit(batch.length),
          ),
        );
        return keywordsSnapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as KeywordFromStore),
        );
      }),
    );
    return concat(...responses);
  }

  static keywordDocQuery(keywordId: string, workspaceId: string, connectorId: string) {
    return doc(db, getWorkspaceKeywordsPath(workspaceId, connectorId), keywordId);
  }

  private static extractTagsIds(row: RawKeywordCSVData, tags: TagFromStore[]) {
    if (row.tags.startsWith('/')) {
      row.tags = row.tags.slice(1);
    }
    if (row.tags.endsWith('/')) {
      row.tags = row.tags.slice(0, -1);
    }
    const parsedTags = row.tags.split('/');
    return parsedTags.map((tag) => {
      const tagIndex = tags.findIndex((t) => t.name.toLowerCase() === tag.toLowerCase());
      if (tagIndex === -1) {
        return undefined;
      }
      return tags[tagIndex].id;
    });
  }
}
